import type { ResultLink } from '@/payment/payment-steps';
import { OUTLINE_PILL, PRIMARY_PILL } from '@/payment/payment-styles';

/**
 * 支払準備のポップアップの状態。
 *
 * - `running`: GAS で動いている。工程と件数の進みを出す
 * - `confirm`: 送信・移動の前に、対象を見せて人に確かめる
 * - `done`: 終わった。作ったレポート・フォルダへのリンクを出す
 * - `error`: 失敗した。理由を出す
 */
export type PaymentDialogState =
  | {
      phase: 'running';
      title: string;
      step: string;
      current: number | null;
      total: number | null;
    }
  | {
      phase: 'confirm';
      title: string;
      message: string;
      items: readonly string[];
      confirmLabel: string;
      onConfirm: () => void;
    }
  | { phase: 'done'; title: string; message: string; links: readonly ResultLink[] }
  | { phase: 'error'; title: string; message: string };

/**
 * PaymentProgressDialog の props。
 */
export interface PaymentProgressDialogProps {
  /** 出す内容 */
  state: PaymentDialogState;
  /** 閉じるときに呼ばれる（動いている間は閉じられない） */
  onClose: () => void;
}

/** 確認で一覧に出す件数の上限。多いときは「他 n 社」とまとめる */
/** @graph-connects none */
const MAX_LISTED = 15;

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払準備の実行のポップアップ。動いている間は工程と件数の進みを、送信・移動の前は対象の一覧と確認を、終わったら結果とリンクを、失敗したら理由を出す
 * @graph-connects none
 */
export function PaymentProgressDialog({ state, onClose }: PaymentProgressDialogProps) {
  const running = state.phase === 'running';
  const percent =
    state.phase === 'running' && state.current !== null && state.total !== null && state.total > 0
      ? Math.min(100, Math.round((state.current / state.total) * 100))
      : null;

  return (
    <>
      <button
        type="button"
        aria-label="閉じる"
        disabled={running}
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default bg-[var(--color-text-primary)]/20"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={state.title}
        className="fixed top-1/2 left-1/2 z-20 flex max-h-[85vh] w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-7 py-6 shadow-[0_12px_40px_var(--color-shadow)]"
      >
        <h2 className="m-0 mb-3 text-base font-semibold text-[var(--color-text-primary)]">
          {state.title}
        </h2>

        {state.phase === 'running' ? (
          <div role="status">
            <p className="m-0 text-sm text-[var(--color-text-primary)]">{state.step}</p>
            {percent === null ? null : (
              <>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-doing-bg)]">
                  <div
                    className="h-full rounded-full bg-[var(--color-doing-border)]"
                    style={{ width: `${String(percent)}%` }}
                  />
                </div>
                <p className="mt-1 mb-0 text-right text-xs tabular-nums text-[var(--color-text-muted)]">
                  {state.current} / {state.total}
                </p>
              </>
            )}
            <p className="mt-3 mb-0 text-xs text-[var(--color-text-muted)]">
              GAS で動いています。終わるまでこのままお待ちください。
            </p>
          </div>
        ) : null}

        {state.phase === 'confirm' ? (
          <>
            <p className="m-0 text-sm leading-relaxed whitespace-pre-line text-[var(--color-text-primary)]">
              {state.message}
            </p>
            {state.items.length === 0 ? null : (
              <ul className="mt-3 mb-0 max-h-56 list-none overflow-y-auto rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-primary)]">
                {state.items.slice(0, MAX_LISTED).map((item) => (
                  <li key={item}>{item}</li>
                ))}
                {state.items.length > MAX_LISTED ? (
                  <li className="text-[var(--color-text-muted)]">
                    …他 {state.items.length - MAX_LISTED} 社
                  </li>
                ) : null}
              </ul>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={onClose} className={OUTLINE_PILL}>
                取りやめる
              </button>
              <button type="button" onClick={state.onConfirm} className={PRIMARY_PILL}>
                {state.confirmLabel}
              </button>
            </div>
          </>
        ) : null}

        {state.phase === 'done' || state.phase === 'error' ? (
          <>
            <p
              role="status"
              className={
                state.phase === 'error'
                  ? 'm-0 text-sm leading-relaxed whitespace-pre-line text-[var(--color-overdue)]'
                  : 'm-0 text-sm leading-relaxed whitespace-pre-line text-[var(--color-text-primary)]'
              }
            >
              {state.message}
            </p>
            {state.phase === 'done' && state.links.length > 0 ? (
              <ul className="mt-3 mb-0 list-none space-y-1 p-0 text-sm">
                {state.links.map((link) => (
                  <li key={link.url}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--color-accent)] underline"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} className={OUTLINE_PILL}>
                閉じる
              </button>
            </div>
          </>
        ) : null}
      </aside>
    </>
  );
}
